import { Component } from "@angular/core";
import { Router } from "@angular/router";
import { AbstractControl, FormBuilder, FormGroup, Validators } from "@angular/forms";
import dayjs from "dayjs";
import * as momentTZ from "moment-timezone";
import isBetween from "dayjs/plugin/isBetween";
import { AppConstants } from "@core/constants/app";
import { ApplicationProgressResponse } from "@core/interfaces/api-responses/application-progress-response";
import { ApplicationService } from "@core/data/application.service";
import { UtilService } from "@core/data/util.service";
import { take } from "rxjs/operators";
import { ApplicationConfigService } from "@core/data/application-config.service";
import { MedicareConfig } from "@core/interfaces/api-responses/medicare-config";

@Component({
    selector: "app-eligibility-check",
    templateUrl: "./eligibility-check.component.html",
    styleUrls: ["./eligibility-check.component.scss"],
    standalone: false,
})
export class EligibilityCheckComponent {
    eligibilityCheckForm: FormGroup;
    translationPrefix = "pages.eligibility_check";
    private medicareConfig: MedicareConfig;
    readonly backButtonTranslation = "wizard.actions.backToDashboard";

    private static getFormConfig(params: { effectiveDateCutOffMonths: number }) {
        return {
            requestedEffectiveDate: [
                null,
                {
                    validators: [Validators.required, EligibilityCheckComponent.validateDateLogic(params.effectiveDateCutOffMonths)],
                    updateOn: "blur",
                },
            ],
        };
    }

    private static validateDateLogic(effectiveDateCutOffMonths: number) {
        return (formControl: AbstractControl) => {
            if (!formControl.value || !formControl.value.length || formControl.value.length !== 10) {
                return null;
            }
            const value = dayjs(formControl.value, "MM/DD/YYYY", true).startOf("day");

            if (!value.isValid()) {
                return { invalidDate: { valid: false } };
            }

            if (value.date() !== 1) {
                return { firstDayOfMonth: { valid: false } };
            }
            const start = momentTZ.tz("America/New_York").startOf("month").add(1, "month");
            const end = start
                .clone()
                .add(effectiveDateCutOffMonths <= 0 ? 0 : effectiveDateCutOffMonths - 1, "months")
                .endOf("day");
            return !value.isBetween(start.toDate(), end.toDate(), null, "[]")
                ? {
                      dateRange: {
                          valid: false,
                          min: start.format("MM/DD/YYYY"),
                          max: end.format("MM/DD/YYYY"),
                      },
                  }
                : null;
        };
    }

    constructor(
        private router: Router,
        private utilService: UtilService,
        private applicationService: ApplicationService,
        private fb: FormBuilder,
        private applicationConfigService: ApplicationConfigService,
    ) {
        dayjs.extend(isBetween);
        this.applicationConfigService.clearConfig();
        this.utilService
            .getMedicareEnrollmentConfig()
            .pipe(take(1))
            .subscribe((response) => {
                this.medicareConfig = response;
                this.eligibilityCheckForm = this.fb.group(
                    EligibilityCheckComponent.getFormConfig({
                        effectiveDateCutOffMonths: +response.EFFECTIVE_DATE_CUTOFF_MONTHS,
                    }),
                );
            });
    }

    goBackToDashboard(): void {
        this.router.navigate([AppConstants.DASHBOARD_PAGE], {
            state: { skipLanguageModal: true },
        });
    }

    get requestedEffectiveDate() {
        return this.eligibilityCheckForm.get("requestedEffectiveDate");
    }

    resolveApplicationYear() {
        this.eligibilityCheckForm.markAllAsTouched();
        if (this.eligibilityCheckForm.invalid) {
            return;
        }
        const formattedEffectiveDate = dayjs(this.requestedEffectiveDate.value, "MM/DD/YYYY").format("YYYY-MM-DD");
        this.applicationService
            .resolveApplicationYear(formattedEffectiveDate)
            .pipe(take(1))
            .subscribe((response: ApplicationProgressResponse) => {
                const year = dayjs(formattedEffectiveDate, "YYYY-MM-DD").year();
                this.applicationConfigService.setConfig(null, formattedEffectiveDate, this.medicareConfig?.REVISION?.[year] ?? null);
                if (response?.status === AppConstants.APP_STATUS_SUBMITTED) {
                    this.router.navigate([AppConstants.CONFIRMATION_PAGE, response.id]);
                    return;
                }
                this.router.navigate([AppConstants.INTRO_PAGE], {
                    state: {
                        requestedEffectiveDate: formattedEffectiveDate,
                        application: response,
                    },
                });
            });
    }
}
